class CompleteTheLook extends HTMLElement {
  constructor() {
    super();
    this.eventController = null;
  }

  connectedCallback() {
    this.eventController?.abort();
    this.eventController = new AbortController();
    this.eventSignal = this.eventController.signal;
    this.popup = this.querySelector('.js-complete-the-look__popup');
    this.counters = new Set();
    this.items = [...this.querySelectorAll('.js-additional-product')].flatMap((element) => {
      const productJson = element.querySelector('.js-additional-product__json');
      const siblingsJson = element.querySelector('.js-additional-product__siblings-json');
      let product;
      let siblings = null;

      try {
        product = JSON.parse(productJson?.textContent || '');
        siblings = siblingsJson ? JSON.parse(siblingsJson.textContent) : null;
      } catch (error) {
        console.error('Complete the look: invalid product data', error);
        return [];
      }

      const options = [...element.querySelectorAll('.js-additional-product__option')];
      const item = {
        element,
        inner: element.querySelector('.js-additional-product__inner'),
        product,
        siblings,
        options,
        checkbox: element.querySelector('.js-additional-product__checkbox'),
        priceOld: element.querySelector('.js-additional-product__old-price'),
        priceCurrent: element.querySelector('.js-additional-product__current-price'),
        priceDiscount: element.querySelector('.js-additional-product__discount'),
        availability: element.querySelector('.js-additional-product__availability'),
        summary: element.querySelector('.js-additional-product__summary'),
        panel: element.querySelector('.js-additional-product__options-panel'),
        toggle: element.querySelector('.js-additional-product__toggle'),
        variant: null
      };

      if (!item.checkbox || !item.priceCurrent) return [];

      options.forEach((option) => this.bindOptionButtons(item, option));
      element.querySelectorAll('.js-additional-product__sibling-color').forEach((button) => {
        button.addEventListener('click', () => this.switchSiblingProduct(item, button.value), { signal: this.eventSignal });
      });
      this.applyColorSwatches(element);
      item.checkbox.addEventListener('change', () => {
        this.updateSelectedState(item);
        this.updateButton();
      }, { signal: this.eventSignal });
      item.toggle?.addEventListener('click', () => this.toggleOptions(item), { signal: this.eventSignal });
      element.querySelector('.js-additional-product__done')?.addEventListener('click', () => {
        if (!item.variant?.available) return;
        item.checkbox.checked = true;
        this.updateSelectedState(item);
        this.closeOptions(item);
        this.updateButton();
      }, { signal: this.eventSignal });
      element.querySelector('.js-additional-product__popup-open')?.addEventListener('click', () => this.openPopup(item), { signal: this.eventSignal });
      element.querySelectorAll('.js-additional-product__size-guide').forEach((button) => {
        button.addEventListener('click', () => this.openSizeGuide(), { signal: this.eventSignal });
      });
      this.updateItem(item);
      return [item];
    });

    this.handleMainAddToCart = (event) => {
      const button = event.target.closest('.js-main-product__add-to-cart');
      if (!button) return;
      this.pendingAdditionalProducts = this.items.some((item) =>
        item.checkbox.checked && item.variant?.available
      );
      window.clearTimeout(this.pendingResetTimer);
      if (this.pendingAdditionalProducts) {
        this.pendingResetTimer = window.setTimeout(() => {
          this.pendingAdditionalProducts = false;
        }, 10000);
      }
    };
    this.handleMainCartUpdate = () => {
      if (!this.pendingAdditionalProducts) return;
      this.pendingAdditionalProducts = false;
      window.clearTimeout(this.pendingResetTimer);
      this.addSelected();
    };
    document.addEventListener('click', this.handleMainAddToCart, { signal: this.eventSignal });
    window.addEventListener('cart-update', this.handleMainCartUpdate, { signal: this.eventSignal });
    this.querySelectorAll('.js-complete-the-look__popup-close').forEach((button) =>
      button.addEventListener('click', () => this.closePopup(), { signal: this.eventSignal })
    );
    this.querySelector('.js-complete-the-look__popup-prev')?.addEventListener('click', () => this.changePopupImage(-1), { signal: this.eventSignal });
    this.querySelector('.js-complete-the-look__popup-next')?.addEventListener('click', () => this.changePopupImage(1), { signal: this.eventSignal });
    this.popupDetailsToggle = this.querySelector('.js-complete-the-look__popup-details-toggle');
    this.popupDetailsContent = this.querySelector('.js-complete-the-look__popup-details-content');
    this.popupDetailsToggle?.addEventListener('click', () => this.togglePopupDetails(), { signal: this.eventSignal });
    this.handleKeydown = (event) => {
      if (!this.popup || this.popup.hidden) return;
      if (event.key === 'Escape') this.closePopup();
      if (event.key === 'Tab') this.trapPopupFocus(event);
    };
    document.addEventListener('keydown', this.handleKeydown, { signal: this.eventSignal });
    this.updateButton();
  }

  disconnectedCallback() {
    this.eventController?.abort();
    this.eventController = null;
    this.eventSignal = null;
    this.pendingAdditionalProducts = false;
    window.clearTimeout(this.pendingResetTimer);
    this.releaseBodyLock();
    this.counters?.forEach((counter) => counter.remove());
    this.counters?.clear();
  }

  toggleOptions(item) {
    if (!item.panel || !item.toggle) return;
    const open = !item.panel.classList.contains('is-open');
    this.items.forEach((other) => this.closeOptions(other));
    item.panel.classList.toggle('is-open', open);
    item.panel.setAttribute('aria-hidden', String(!open));
    item.toggle.setAttribute('aria-expanded', String(open));
  }

  closeOptions(item) {
    if (!item.panel) return;
    item.panel.classList.remove('is-open');
    item.panel.setAttribute('aria-hidden', 'true');
    item.toggle?.setAttribute('aria-expanded', 'false');
  }

  openSizeGuide() {
    const sizeGuide = document.getElementById('size-guide');
    if (!sizeGuide) return;
    sizeGuide.classList.add('visible');
    document.body.classList.add('locked');
  }

  updateSelectedState(item) {
    item.inner?.classList.toggle('is-selected', item.checkbox.checked);
  }

  bindOptionButtons(item, option) {
    option.querySelectorAll('.js-additional-product__option-value').forEach((button) => {
      button.addEventListener('click', () => {
        this.selectOption(option, button.value);
        this.updateItem(item);
      }, { signal: this.eventSignal });
    });
  }

  switchSiblingProduct(item, handle) {
    const product = item.siblings?.[handle];
    if (!product || product.id === item.product.id) return;

    item.product = product;
    const fallbackVariant = product.variants.find((variant) => variant.available) || product.variants[0];
    item.options.forEach((option, optionIndex) => {
      const values = [...new Set(product.variants.map((variant) => variant.options[optionIndex]).filter(Boolean))];
      const selectedValue = values.includes(this.getSelectedOptions(item.options)[optionIndex])
        ? this.getSelectedOptions(item.options)[optionIndex]
        : fallbackVariant.options[optionIndex];
      const valuesContainer = option.querySelector('.js-additional-product__option-values');
      const isColor = option.classList.contains('js-additional-product__color-option');
      if (!valuesContainer) return;
      valuesContainer.innerHTML = '';

      values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.value = value;
        button.title = value;
        button.setAttribute('aria-pressed', String(value === selectedValue));
        button.className = isColor
          ? 'js-additional-product__option-value pdp__color-swatch pdp-option-color__item'
          : 'js-additional-product__option-value pdp-option-item__btn';
        if (isColor) {
          button.innerHTML = `<span class="pdp__color-swatch-dot js-additional-product__color-swatch" data-color-swatch="${this.escapeHtml(value)}"></span><span class="visually-hidden">${this.escapeHtml(value)}</span>`;
        } else {
          button.innerHTML = `<span class="pdp-option-item__label js-additional-product__option-label">${this.escapeHtml(value)}</span>`;
        }
        valuesContainer.append(button);
      });
      this.selectOption(option, selectedValue);
      this.bindOptionButtons(item, option);
    });

    item.element.querySelectorAll('.js-additional-product__sibling-color').forEach((button) => {
      const selected = button.value === handle;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-selected', selected);
    });
    const title = item.element.querySelector('.js-additional-product__title');
    if (title) {
      title.textContent = product.title;
      title.href = this.getProductUrl(product, handle);
    }
    const image = item.element.querySelector('.js-additional-product__popup-open img');
    if (image && product.featured_image) image.src = this.withImageWidth(product.featured_image, 300);
    this.applyColorSwatches(item.element);
    this.updateItem(item);
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  selectOption(option, value) {
    option.querySelectorAll('.js-additional-product__option-value').forEach((button) => {
      const selected = button.value === value;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-selected', selected);
    });
  }

  getSelectedOptions(options) {
    return options.map((option) => option.querySelector('.js-additional-product__option-value[aria-pressed="true"]')?.value);
  }

  applyColorSwatches(scope) {
    scope.querySelectorAll('.js-additional-product__color-swatch').forEach((swatch) => {
      const value = swatch.dataset.colorSwatch;
      const color = (window.productColors || []).find((entry) =>
        entry.name?.toLowerCase().trim() === value.toLowerCase().trim()
      );
      if (color?.image) {
        swatch.style.backgroundImage = `url(${color.image})`;
      } else {
        swatch.style.backgroundColor = color?.color || value;
      }
    });
  }

  updateItem(item) {
    const selectedOptions = this.getSelectedOptions(item.options);
    item.variant = item.product.variants.find((variant) =>
      variant.options.every((value, index) => value === selectedOptions[index])
    ) || item.product.variants[0];

    if (!item.variant) {
      item.checkbox.checked = false;
      item.checkbox.disabled = true;
      if (item.availability) {
        item.availability.hidden = false;
        item.availability.textContent = this.dataset.soldOutText;
      }
      this.updateSelectedState(item);
      this.updateButton();
      return;
    }

    const available = Boolean(item.variant?.available);
    item.checkbox.disabled = !available;
    if (!available) item.checkbox.checked = false;
    this.updateSelectedState(item);
    if (item.availability) {
      item.availability.hidden = available;
      item.availability.textContent = this.dataset.soldOutText;
    }
    this.updatePrice({
      oldPrice: item.priceOld,
      currentPrice: item.priceCurrent,
      discount: item.priceDiscount
    }, item.variant);
    if (item.summary) item.summary.textContent = selectedOptions.join(' / ');

    item.options.forEach((option, optionIndex) => {
      option.querySelectorAll('.js-additional-product__option-value').forEach((button) => {
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = button.value;
        const optionAvailable = item.product.variants.some((variant) =>
          variant.available && variant.options.every((value, index) => value === candidateOptions[index])
        );
        button.disabled = !optionAvailable;
        button.classList.toggle('is-disabled', !optionAvailable);
        button.querySelector('.js-additional-product__option-label')?.classList.toggle('disabled', !optionAvailable);
      });
    });

    if (item.variant.featured_image?.src) {
      const image = item.element.querySelector('.js-additional-product__popup-open img');
      if (image) image.src = this.withImageWidth(item.variant.featured_image.src, 300);
    }
    this.updateButton();
  }

  openPopup(item) {
    if (!this.popup || !item.variant) return;
    this.popupItem = item;
    this.popupImageIndex = 0;
    this.querySelector('.js-complete-the-look__popup-title').textContent = item.product.title;
    this.querySelector('.js-complete-the-look__popup-link-title').textContent = item.product.title;
    this.querySelector('.js-complete-the-look__popup-description').innerHTML = item.product.description || '';
    this.querySelector('.js-complete-the-look__popup-link').href = this.getProductUrl(item.product);
    this.closePopupDetails();

    const optionsContainer = this.querySelector('.js-complete-the-look__popup-options');
    optionsContainer.innerHTML = '';
    const siblingColors = item.element.querySelector('.js-additional-product__sibling-color-option');
    if (siblingColors) {
      const option = siblingColors.cloneNode(true);
      option.classList.add('additional-product-popup__option');
      option.querySelectorAll('.js-additional-product__sibling-color').forEach((button) => {
        button.addEventListener('click', () => {
          const siblingProduct = item.siblings?.[button.value];
          if (siblingProduct) {
            window.location.href = this.getProductUrl(siblingProduct, button.value);
          }
        }, { signal: this.eventSignal });
      });
      optionsContainer.append(option);
    } else {
      const colorOption = item.options.find((option) => option.classList.contains('js-additional-product__color-option'));
      if (colorOption) {
        const option = colorOption.cloneNode(true);
        option.hidden = false;
        option.classList.remove('additional-product__option--variant-color-hidden');
        option.classList.add('additional-product-popup__option');
        option.querySelectorAll('.js-additional-product__option-value').forEach((button) => {
          button.addEventListener('click', () => {
            const colorIndex = Number(option.dataset.optionIndex);
            const variant = item.product.variants.find((candidate) =>
              candidate.available && candidate.options[colorIndex] === button.value
            ) || item.product.variants.find((candidate) => candidate.options[colorIndex] === button.value);
            if (variant) {
              const productUrl = new URL(this.getProductUrl(item.product), window.location.origin);
              productUrl.searchParams.set('variant', variant.id);
              window.location.href = productUrl.href;
            }
          }, { signal: this.eventSignal });
        });
        optionsContainer.append(option);
      }
    }
    this.applyColorSwatches(optionsContainer);

    this.popup.hidden = false;
    this.acquireBodyLock();
    this.popupVariant = item.variant;
    this.updatePrice({
      oldPrice: this.querySelector('.js-complete-the-look__popup-old-price'),
      currentPrice: this.querySelector('.js-complete-the-look__popup-current-price'),
      discount: this.querySelector('.js-complete-the-look__popup-discount')
    }, item.variant);
    const variantImage = item.variant.featured_image?.src;
    const media = (item.product.media || item.product.images || [])
      .map((image) => typeof image === 'string' ? image : image.src)
      .filter(Boolean);
    this.popupImages = variantImage ? [variantImage, ...media.filter((src) => src !== variantImage)] : media;
    if (!this.popupImages.length && item.product.featured_image) this.popupImages = [item.product.featured_image];
    this.renderPopupImage();
    this.updatePopupControls();
    this.querySelector('.js-complete-the-look__popup-close')?.focus();
  }

  renderPopupImage() {
    const image = this.querySelector('.js-complete-the-look__popup-image');
    const src = this.popupImages[this.popupImageIndex];
    if (src) {
      image.src = this.withImageWidth(src, 900);
      image.hidden = false;
    } else {
      image.removeAttribute('src');
      image.hidden = true;
    }
    image.alt = this.popupItem.product.title;
    const dots = this.querySelector('.js-complete-the-look__popup-dots');
    dots.innerHTML = this.popupImages.map((_, index) =>
      `<button type="button" aria-label="${index + 1} ${this.dataset.sliderOf} ${this.popupImages.length}" class="${index === this.popupImageIndex ? 'is-active' : ''}"></button>`
    ).join('');
    [...dots.children].forEach((dot, index) => dot.addEventListener('click', () => {
      this.popupImageIndex = index;
      this.renderPopupImage();
    }, { signal: this.eventSignal }));
  }

  changePopupImage(direction) {
    if (this.popupImages.length < 2) return;
    this.popupImageIndex = (this.popupImageIndex + direction + this.popupImages.length) % this.popupImages.length;
    this.renderPopupImage();
  }

  updatePopupControls() {
    const hidden = this.popupImages.length < 2;
    this.querySelector('.js-complete-the-look__popup-prev')?.toggleAttribute('hidden', hidden);
    this.querySelector('.js-complete-the-look__popup-next')?.toggleAttribute('hidden', hidden);
    this.querySelector('.js-complete-the-look__popup-dots')?.toggleAttribute('hidden', hidden);
  }

  closePopup() {
    if (!this.popup) return;
    this.closePopupDetails();
    this.popup.hidden = true;
    this.releaseBodyLock();
    this.popupItem?.element.querySelector('.js-additional-product__popup-open')?.focus();
  }

  trapPopupFocus(event) {
    const focusable = [...this.popup.querySelectorAll(
      'a[href]:not([hidden]), button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"]):not([hidden])'
    )].filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  togglePopupDetails() {
    const open = this.popupDetailsToggle?.getAttribute('aria-expanded') !== 'true';
    this.popupDetailsToggle?.setAttribute('aria-expanded', String(open));
    this.popupDetailsContent?.classList.toggle('is-open', open);
    this.popupDetailsContent?.setAttribute('aria-hidden', String(!open));
    this.popupDetailsContent?.toggleAttribute('inert', !open);
  }

  closePopupDetails() {
    this.popupDetailsToggle?.setAttribute('aria-expanded', 'false');
    this.popupDetailsContent?.classList.remove('is-open');
    this.popupDetailsContent?.setAttribute('aria-hidden', 'true');
    this.popupDetailsContent?.setAttribute('inert', '');
  }

  updateButton() {
    if (!this.items) return;
    const additionalCount = this.items.filter((item) =>
      item.checkbox.checked && item.variant?.available
    ).length;
    const totalCount = additionalCount + 1;

    document.querySelectorAll('.js-main-product__add-to-cart').forEach((button) => {
      let counter = button.querySelector('.js-complete-the-look__counter');
      if (!counter) {
        counter = document.createElement('span');
        counter.className = 'complete-the-look__counter js-complete-the-look__counter';
        counter.setAttribute('aria-hidden', 'true');
        button.append(counter);
        this.counters.add(counter);
      }
      counter.hidden = additionalCount === 0;
      counter.textContent = additionalCount > 0 ? ` (${totalCount})` : '';
    });
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency
    }).format(cents / 100);
  }

  updatePrice(elements, variant) {
    const price = Number(variant?.price || 0);
    const compareAtPrice = Number(variant?.compare_at_price || 0);
    const hasDiscount = compareAtPrice > price;

    if (elements.oldPrice) {
      elements.oldPrice.textContent = hasDiscount ? this.formatMoney(compareAtPrice) : '';
      elements.oldPrice.hidden = !hasDiscount;
    }
    if (elements.currentPrice) {
      elements.currentPrice.textContent = this.formatMoney(price);
      elements.currentPrice.classList.toggle('product-price__price--active', hasDiscount);
    }
    if (elements.discount) {
      const discountValue = hasDiscount ? Math.round(100 - (price / compareAtPrice) * 100) : 0;
      elements.discount.textContent = hasDiscount ? `(-${discountValue}%)` : '';
      elements.discount.hidden = !hasDiscount;
    }
  }

  withImageWidth(src, width) {
    if (!src) return '';
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=${width}`;
  }

  getProductUrl(product, fallbackHandle = '') {
    if (product?.url && product.url !== 'undefined') return product.url;

    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const handle = product?.handle || fallbackHandle;
    return `${normalizedRoot}products/${encodeURIComponent(handle)}`;
  }

  acquireBodyLock() {
    this.ownsBodyLock = !document.body.classList.contains('locked');
    document.body.classList.add('locked');
  }

  releaseBodyLock() {
    if (this.ownsBodyLock) document.body.classList.remove('locked');
    this.ownsBodyLock = false;
  }

  async addSelected() {
    const items = this.items
      .filter((item) => item.checkbox.checked && item.variant?.available)
      .map((item) => ({ id: item.variant.id, quantity: 1 }));
    if (!items.length) return;

    try {
      const signal = this.eventSignal;
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
        signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      this.items.forEach((item) => {
        item.checkbox.checked = false;
        this.updateSelectedState(item);
      });
      this.updateButton();
      window.dispatchEvent(new CustomEvent('cart-update', { detail: { openAjaxCart: true } }));
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }
}

if (!customElements.get('c-complete-the-look')) {
  customElements.define('c-complete-the-look', CompleteTheLook);
}
